/**
 * A conversation with Buddy that builds a plan: board N's hero until the first message, then board
 * M's thread over the docked composer, every element drawn from board O.
 *
 * The one component a page mounts. It takes the conversation and answers with the trainer's
 * intents; the page sends them. It owns only what is the trainer's gesture until it is sent: the
 * picks on the open questions. Every rule about them comes
 * from `entities/ai-conversation` (`threadItems`, `composerState`, `answersToSend`, `meterView`)
 * and from this slice's `model/outsideCatalogue.ts`; this file wires them to the cards.
 */

import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  answersToSend,
  composerState,
  type AiChangeDecision,
  type AiSheetListing,
  decisionKey,
  everyQuestionPicked,
  meterView,
  threadItems,
  type AiBuildResult,
  type AiConversation,
  type AiIntent,
  type AiUnresolvedExercise,
  type BuddyBody,
  type QuestionPicks,
} from '~/entities/ai-conversation';
import { proposalFromWire } from '~/entities/plan-draft';
import { PearlMark } from '~/shared/ui/PearlMark';

import { resetWords } from '../lib/limitWording';
import { liveStepWords } from '../lib/planWording';
import { useExerciseDecisions, type ExerciseDecisionHost, type QuickAddOutcome } from '../lib/useExerciseDecisions';
import { changingPicks } from '../model/changingPicks';

import { BuiltDraft, FailureNotice, type DraftSessionGroup, type DraftSessionRow } from './BuildReport';
import { DraftFeedback } from './DraftFeedback';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';

import { ErrorGlyph } from './ChatGlyphs';
import { Composer } from './Composer';
import { AnswersBubble, BuddyMessage, Disclosure, ModelText, TrainerBubble } from './Messages';
import { OutlineCard } from './OutlineCard';
import { OutsideCatalogue, PickChangeCard } from './OutsideCatalogue';
import { ProposalCard, type ExerciseChangeSlot, type ProposalHost } from './ProposalCard';
import { QuestionsCard } from './QuestionsCard';
import { SheetImport, ValuesToDecide, type SheetImportProps } from './SheetImport';
import { UsageMeter } from './UsageMeter';
import surfaces from './surfaces.module.css';
import styles from './BuddyChat.module.css';

export interface BuddyChatProps {
  readonly conversation: AiConversation;
  /** Send what the trainer asked for; answers whether the server took it. */
  readonly onSend: (intent: AiIntent) => Promise<boolean>;
  /** The working composer's stop, on the turn that is running. */
  readonly onStop: (turnId: number) => void;
  /** Cancel the outline waiting for approval; the server closes it, so a reload does not reopen it. */
  readonly onDismissOutline: (turnId: number) => void;
  /** Where the exercises outside the catalogue are decided and added (R8; board E). */
  readonly exercises: ExerciseDecisionHost;
  /** The row of the list to open first: one the builder's panel handed over to this page. */
  readonly openRow?: { readonly turnId: number; readonly name: string } | null;
  /** The decisions on their way to the server, each under its `decisionKey`. */
  readonly deciding: ReadonlySet<string>;
  /**
   * Opens a finished draft in the builder, once every unresolved exercise is decided: on the
   * trainer's press, and on the Buddy page by itself after their last decision (board E).
   */
  readonly onOpenDraft: (turnId: number, result: AiBuildResult) => void;
  /** The sessions of one draft, for its card's list (boards Q, R); the page reads them off the plan. */
  readonly sessionsOf: (turnId: number, result: AiBuildResult) => readonly DraftSessionGroup[];
  /** The session whose panel is open beside the conversation, and the draft it belongs to. */
  readonly openSession: { readonly turnId: number; readonly daySlotToken: string } | null;
  readonly onOpenSession: (turnId: number, daySlotToken: string) => void;
  /** The session row owed focus: the one whose panel just closed. */
  readonly returnFocusSession: string | null;
  /**
   * Put focus in the composer: the conversation was just started with „Nowa rozmowa". A number that
   * moves on every press; `null` when nothing asked.
   */
  readonly focusComposer: number | null;
  /** The trainer rates a draft, with a comment or without (R47); answers whether it was taken. */
  readonly onFeedback: (turnId: number, helpful: boolean, comment: string | null) => Promise<boolean>;
  /** What the last act could not do, already worded, drawn over the composer; `null` when nothing failed. */
  readonly notice: string | null;
  /** The Buddy page (board M) or the builder's assistant panel (boards I, S). */
  readonly layout?: 'page' | 'panel';
  /** Where a proposal's changes are applied (boards S, S3). */
  readonly proposalHost: ProposalHost;
  /** The trainer's decisions on changes of proposal `turnId`, against the version it was made on (R53). */
  readonly onDecideChanges: (turnId: number, snapshotVersion: string, decisions: ReadonlyMap<string, AiChangeDecision>) => void;
  /** The spreadsheet import (board T): the listing and the import; `null` where none is offered. */
  readonly importer: {
    readonly list: (file: File) => Promise<AiSheetListing>;
    readonly send: SheetImportProps['onImport'];
  } | null;
}

export type { DraftSessionGroup, DraftSessionRow, ExerciseChangeSlot, ExerciseDecisionHost, ProposalHost, QuickAddOutcome };

export function BuddyChat({
  conversation,
  onSend,
  onStop,
  onDismissOutline,
  exercises,
  openRow = null,
  deciding,
  onOpenDraft,
  sessionsOf,
  openSession,
  onOpenSession,
  returnFocusSession,
  focusComposer,
  onFeedback,
  notice,
  layout = 'page',
  proposalHost,
  onDecideChanges,
  importer,
}: BuddyChatProps) {
  const { t, i18n } = useTranslation();
  const composer = composerState(conversation);
  const panel = layout === 'panel';
  // The spreadsheet import (board T): open from the tray or a file dragged over the conversation.
  const [importOpen, setImportOpen] = useState(false);
  // The thread stands from the first message; the builder's panel has no hero and stands at once.
  // An import draws in the thread too (board T), so opening one on the hero brings the thread up.
  const started = composer.started || panel || (importer !== null && importOpen);
  // „Odrzuć z uwagą": the next message says what to change about this change (R16).
  const [noted, setRejecting] = useState<{ readonly turnId: number; readonly changeId: string; readonly title: string } | null>(null);
  // The note stands while its proposal waits: one a later message replaced takes it with it.
  const rejecting = noted !== null && noted.turnId === conversation.waitingProposalTurnId ? noted : null;
  const [dragging, setDragging] = useState(false);
  const [dropped, setDropped] = useState<File | null>(null);
  // Buddy's own picks the trainer opened to change (R8), each under its `decisionKey`, drawn under
  // „Założyłem". A decision moves the name into the draft's unresolved exercises; the key stays, so
  // the decided card stands where it was opened.
  const [changing, setChanging] = useState<ReadonlySet<string>>(new Set());
  const pickIds = useId();
  const pickCardId = (turnId: number, name: string) => `${pickIds}-${String(turnId)}-${encodeURIComponent(name)}`;
  const items = threadItems(conversation);
  // What the thread held at its first paint: those messages stand still, only newer ones enter.
  const [present] = useState(() => new Set(items.map((item) => item.id)));
  // The drafts already built at the first paint; one that lands later is brought into view.
  const [builtAtFirstPaint] = useState(
    () => new Set(items.flatMap((item) => (item.kind === 'buddy' && item.body.kind === 'build' && item.body.result !== null ? [item.turnId] : []))),
  );
  const meter = meterView(conversation.meter);
  // The clock the reset time is worded against („o 14:20" today, „w czwartek" on another day). It
  // moves on every half minute, so a screen left open over midnight says the right day.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = window.setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      window.clearInterval(tick);
    };
  }, []);
  const field = useRef<HTMLTextAreaElement>(null);
  const outlineCard = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (focusComposer !== null) field.current?.focus();
  }, [focusComposer]);

  // The picks belong to the questions of one turn; a new set of questions starts with none.
  const questionsTurn = composer.openQuestionsTurnId;
  const [picked, setPicked] = useState<{ readonly turnId: number | null; readonly picks: QuestionPicks }>({
    turnId: null,
    picks: {},
  });
  const picks = picked.turnId !== null && picked.turnId === questionsTurn ? picked.picks : {};

  const when = resetWords(t, conversation.meter.resetsAt, now, i18n.language);
  const placeholder =
    composer.phase === 'blocked'
      ? when === null
        ? t('web:aiPlan.composer.placeholderBlockedLater')
        : t('web:aiPlan.composer.placeholderBlocked', { when })
      : rejecting !== null
        ? t('web:aiPlan.composer.placeholderReject', { title: rejecting.title })
        : panel
          ? t('web:aiPlan.composer.placeholderPanel')
          : composer.started
            ? t('web:aiPlan.composer.placeholderReply')
            : t('web:aiPlan.composer.placeholderHero');

  const sendText = (text: string, requestId: string): Promise<boolean> => {
    if (composer.phase !== 'ready') return Promise.resolve(false);
    const { openQuestions } = composer;
    const answers = openQuestions === null ? null : answersToSend(openQuestions, picks, text);
    if (answers === null) {
      const rejected = rejecting;
      return onSend({
        kind: 'message',
        text: text.trim(),
        requestId,
        ...(rejected === null ? {} : { rejectedChangeId: rejected.changeId }),
      }).then((taken) => {
        if (taken) setRejecting(null);
        return taken;
      });
    }
    // Every question already picked: the words typed go WITH the answers rather than being dropped.
    const allPicked = openQuestions !== null && everyQuestionPicked(openQuestions, picks);
    return onSend({ kind: 'answers', answers, requestId, ...(allPicked ? { text: text.trim() } : {}) });
  };
  // A card whose own control goes with the press hands focus to the composer, as „Anuluj" does, so
  // focus never falls to the page.
  const toComposer = () => {
    field.current?.focus();
  };
  // Board E's decisions on every draft of the thread, and the draft opening by itself after the
  // last of them — on the Buddy page; the builder's panel hands a draft to the page instead.
  const outside = useExerciseDecisions({ conversation, host: exercises, deciding, onOpenDraft, autoOpen: !panel, initialRow: openRow });
  // One of Buddy's own picks the trainer opened to change, on the draft of `turnId` (R8), which
  // `onLeave` closes with nothing sent while it is still Buddy's.
  const pickCard = (turnId: number, exercise: AiUnresolvedExercise, onLeave?: () => void) => (
    <PickChangeCard
      key={exercise.name}
      id={pickCardId(turnId, exercise.name)}
      decisions={outside.forChanges(turnId, [exercise])}
      onLeave={onLeave}
    />
  );
  const stop = () => {
    if (composer.activeTurnId !== null) onStop(composer.activeTurnId);
  };

  const statusLine = liveStepWords(t, composer.live);
  const failure = notice !== null ? (
    <p className={surfaces.notice} role="alert">
      <ErrorGlyph className={`${String(surfaces.noticeGlyph)} ${String(styles.bad)}`} />
      <span>{notice}</span>
    </p>
  ) : null;

  const ready = composer.phase === 'ready';
  const body = (turnId: number, content: BuddyBody) => {
    switch (content.kind) {
      case 'thinking':
        // Nothing produced yet: the message's live line is all it shows.
        return null;
      case 'text':
        return <ModelText text={content.text} />;
      case 'proposal': {
        const proposal = proposalFromWire(content.proposal);
        if (proposal === null) return <ModelText text={content.proposal.summary} />;
        return (
          <>
            <ModelText text={proposal.summary} />
            <ProposalCard
              proposal={proposal}
              standing={content.standing}
              recorded={content.decisions}
              draftTurnId={content.draftTurnId}
              host={proposalHost}
              ready={ready}
              onRecord={(decisions) => {
                onDecideChanges(turnId, proposal.snapshotVersion, decisions);
              }}
              onRejectWithReason={(changeId, title) => {
                setRejecting({ turnId, changeId, title });
                toComposer();
              }}
              onAnotherVersion={
                content.anotherVersion && ready
                  ? () => {
                      toComposer();
                      void onSend({ kind: 'retry', turnId });
                    }
                  : null
              }
            />
            <DraftFeedback feedback={content.feedback} onRate={(helpful, comment) => onFeedback(turnId, helpful, comment)} subject="proposal" />
          </>
        );
      }
      case 'failed':
        return (
          <FailureNotice
            failure={null}
            unavailable={content.unavailable}
            stepLimit={content.stepLimit}
            retryable={content.retryable && ready}
            onRetry={() => {
              toComposer();
              void onSend({ kind: 'retry', turnId });
            }}
            onBackToOutline={null}
          />
        );
      case 'questions':
        return (
          <QuestionsCard
            intro={content.intro}
            questions={content.questions}
            open={content.open && ready}
            picks={picks}
            onPick={(questionId, optionId) => {
              setPicked({ turnId, picks: { ...picks, [questionId]: optionId } });
            }}
            onAnswer={() => {
              const answers = answersToSend(content.questions, picks, '');
              if (answers === null) return;
              toComposer();
              void onSend({ kind: 'answers', answers, requestId: crypto.randomUUID() });
            }}
            onSkip={() => {
              toComposer();
              void onSend({ kind: 'skipQuestions', requestId: crypto.randomUUID() });
            }}
          />
        );
      case 'outline':
        return (
          <OutlineCard
            outline={content.outline}
            actionable={content.open && ready}
            onCancel={() => {
              toComposer();
              onDismissOutline(turnId);
            }}
            onBuild={() => {
              toComposer();
              void onSend({ kind: 'approveOutline', turnId });
            }}
            cardRef={(node) => {
              outlineCard.current = node;
            }}
          />
        );
      case 'build': {
        const { result } = content;
        // The names of the list: a pick of Buddy's own opened to change stays under „Założyłem".
        const listed = result === null ? [] : result.unresolved.filter((exercise) => !changing.has(decisionKey(turnId, exercise.name)));
        const decisions = outside.forDraft(turnId, listed);
        return (
          <>
            {content.failed && (
              <FailureNotice
                failure={content.failure}
                unavailable={content.unavailable}
                retryable={content.retryable && ready}
                onRetry={() => {
                  toComposer();
                  void onSend({ kind: 'retry', turnId });
                }}
                onBackToOutline={() => {
                  outlineCard.current?.scrollIntoView({ block: 'center' });
                  outlineCard.current?.focus({ preventScroll: true });
                }}
              />
            )}
            {result !== null && result.valuesToDecide.length > 0 && <ValuesToDecide values={result.valuesToDecide} />}
            {result !== null && content.version !== null && (
                  <BuiltDraft
                    importedFrom={content.importedFrom}
                    result={result}
                    version={content.version}
                    sessions={sessionsOf(turnId, result)}
                    openSessionToken={openSession?.turnId === turnId ? openSession.daySlotToken : null}
                    onOpenSession={(daySlotToken) => {
                      onOpenSession(turnId, daySlotToken);
                    }}
                    returnFocusToken={returnFocusSession}
                    arrived={!builtAtFirstPaint.has(turnId)}
                    undecided={decisions.view.remaining}
                    onOpen={decisions.open}
                    onRefine={toComposer}
                    onRebuild={
                      content.rebuildable && ready
                        ? () => {
                            toComposer();
                            void onSend({ kind: 'retry', turnId });
                          }
                        : null
                    }
                    feedback={content.feedback}
                    onRate={(helpful, comment) => onFeedback(turnId, helpful, comment)}
                    unresolved={listed.length > 0 ? <OutsideCatalogue decisions={decisions} /> : null}
                    changes={changingPicks(changing, result.unresolved, result.autoMatched, (name) => decisionKey(turnId, name)).map((pick) =>
                      // In the order the cards were opened (`model/changingPicks.ts`): a decided name stays
                      // under „Założyłem" (the server lists it among the unresolved ones then), under the
                      // same key, so the card keeps its node and its place across the decision.
                      pick.kind === 'decided'
                        ? pickCard(turnId, pick.exercise)
                        : pickCard(
                            turnId,
                            {
                              name: pick.match.name,
                              slotTokens: pick.match.slotTokens,
                              days: pick.match.days,
                              candidates: pick.match.candidates,
                              verdict: null,
                              decision: null,
                              problems: [],
                              proposedExercise: null,
                              pickedName: null,
                            },
                            () => {
                              // Nothing sent: the box takes the line back and its „Zmień" takes focus.
                              // Synchronously, so the button focus lands on already reads „Zmień" and is no
                              // longer expanded: a listener hears its new name, not the one it had open.
                              flushSync(() => {
                                setChanging((current) => without(current, decisionKey(turnId, pick.match.name)));
                              });
                              document.getElementById(`${pickCardId(turnId, pick.match.name)}-change`)?.focus();
                            },
                          ),
                    )}
                    picks={{
                      matches: result.autoMatched,
                      open: new Set(
                        result.autoMatched
                          .map((match) => match.name)
                          .filter((name) => changing.has(decisionKey(turnId, name))),
                      ),
                      onChange: (name) => {
                        const key = decisionKey(turnId, name);
                        setChanging((current) => (current.has(key) ? without(current, key) : new Set(current).add(key)));
                      },
                      busy: new Set(result.autoMatched.map((match) => match.name).filter((name) => deciding.has(decisionKey(turnId, name)))),
                      cardId: (name) => pickCardId(turnId, name),
                    }}
                  />
            )}
          </>
        );
      }
    }
  };

  const openImport =
    importer === null
      ? null
      : () => {
          setImportOpen(true);
        };

  // ONE tree for both stages, the dock keyed, so the composer keeps its node (and its focus) when the
  // hero gives way to the thread after the first message.
  const dock = (
    <div key="dock" className={panel ? styles.panelDock : started ? styles.dock : styles.heroDock}>
      {rejecting !== null && (
        <p className={styles.rejecting}>
          <span className={styles.rejectingText}>{t('web:aiPlan.composer.rejecting', { title: rejecting.title })}</span>
          <button
            type="button"
            className={styles.rejectingClear}
            aria-label={t('web:aiPlan.composer.rejectingClear', { title: rejecting.title })}
            onClick={() => {
              setRejecting(null);
              toComposer();
            }}
          >
            <CloseGlyph className={styles.rejectingGlyph} />
          </button>
        </p>
      )}
      <Composer
        layout={panel ? 'panel' : started ? 'dock' : 'hero'}
        phase={composer.phase}
        statusLine={statusLine}
        placeholder={placeholder}
        onSend={sendText}
        onStop={stop}
        fieldRef={field}
        onSheet={openImport}
        resetWhen={when}
        rejecting={rejecting !== null}
        onEscape={
          rejecting === null
            ? null
            : () => {
                setRejecting(null);
              }
        }
      />
      {failure}
      {meter !== null && <UsageMeter meter={meter} now={now} />}
    </div>
  );

  // A file dragged over the conversation opens the import, whose zone takes the drop.
  const dragHandlers =
    importer === null
      ? {}
      : {
          onDragEnter: (event: DragEvent) => {
            if (!event.dataTransfer.types.includes('Files')) return;
            setImportOpen(true);
            setDragging(true);
          },
          onDragOver: (event: DragEvent) => {
            if (event.dataTransfer.types.includes('Files')) event.preventDefault();
          },
          onDragLeave: (event: DragEvent) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
          },
          onDrop: (event: DragEvent) => {
            event.preventDefault();
            setDragging(false);
            const file = event.dataTransfer.files[0];
            if (file !== undefined) setDropped(file);
          },
        };

  return (
    <div className={started ? (panel ? styles.panelStage : styles.threadStage) : styles.heroStage} {...dragHandlers}>
      {started ? (
        <div
          key="thread"
          className={styles.scroller}
          tabIndex={0}
          role="region"
          aria-label={t('web:aiPlan.thread.label')}
        >
          <div className={panel ? styles.panelThread : styles.thread} role="log">
            {!panel && <Disclosure />}
            {items.map((item) => {
              switch (item.kind) {
                case 'trainer':
                  return <TrainerBubble key={item.id} text={item.text} still={present.has(item.id)} />;
                case 'answers':
                  return (
                    <AnswersBubble key={item.id} answers={item.answers} ownWords={item.ownWords} still={present.has(item.id)} />
                  );
                case 'buddy':
                  return (
                    <BuddyMessage key={item.id} progress={item.progress} still={present.has(item.id)}>
                      {body(item.turnId, item.body)}
                    </BuddyMessage>
                  );
              }
            })}
            {/* The import is the thread's newest item (board T): the zone, then the picker as
              * Buddy's message, scrolled with the thread so a long sheet list is never clipped. */}
            {importer !== null && importOpen && (
              <SheetImport
                onList={importer.list}
                onImport={importer.send}
                ready={ready}
                dragging={dragging}
                dropped={dropped}
                onClose={() => {
                  setImportOpen(false);
                  setDropped(null);
                  toComposer();
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div key="hero" className={styles.hero}>
          <PearlMark state={composer.phase === 'working' ? 'work' : 'rest'} className={styles.heroMark} />
          <h2 className={styles.heroTitle}>{t('web:aiPlan.hero.title')}</h2>
        </div>
      )}
      {dock}
    </div>
  );
}

/** `set` without `key`, as a new set. */
function without(set: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(key);
  return next;
}
